import useLocale from 'locale/useLocale';
import { useCallback, useEffect, useState } from 'react';
import { Button, Notice, Tooltip } from '@appkit4/react-components';
import UnforeseenDatesRow from 'pages/SubmissionFormWizard/SubmissionStepThree/UnforeseenSubmissionStepThree/UnforeseenDatesRow/UnforeseenDatesRow';
import { useFormikContext } from 'formik';
import { NewDeclarationFormValues } from 'pages/SubmissionFormWizard/SubmissionFormWizard';
import { useUserDeclarations } from 'contexts/UserDeclarationsProvider/UserDeclarationsProvider';
import {
    UnforeseenAbsence,
    UnforeseenDeclarationResponse,
} from 'api/generated/models';
import { Loading } from '@appkit4/react-components/loading';

export type UnforeseenSubmissionProps = {
    submissionIdForEditFlow?: string;
    previousUnforeseenSubmissionData: UnforeseenDeclarationResponse | undefined;
    previousUnforeseenSubmissionDataIsPending: boolean;
    consecutiveUnforeseenAndSameReason: boolean;
};

const UnforeseenSubmissionStepThree = ({
    submissionIdForEditFlow,
    previousUnforeseenSubmissionData,
    previousUnforeseenSubmissionDataIsPending,
    consecutiveUnforeseenAndSameReason,
}: UnforeseenSubmissionProps) => {
    const { l } = useLocale();
    const { values, setFieldValue } =
        useFormikContext<NewDeclarationFormValues>();

    const { data: userDeclarationsData } = useUserDeclarations();
    const [isDataSet, setIsDataSet] = useState(false);

    const addNewRow = () => {
        const newAbsence = {
            declarationId: undefined,
            startDate: undefined,
            reasonForAbsence: '',
        };
        const updatedAbsences = [
            ...(values.unforeseenAbsences || []),
            newAbsence,
        ];
        setFieldValue('unforeseenAbsences', updatedAbsences);
    };

    const todayTimestamp = Date.now() + 24 * 60 * 60 * 1000;

    const collectDisabledDates = useCallback(() => {
        if (userDeclarationsData === undefined) return [];

        const disabledDates: Date[] = [];

        // disabled dates from unforeseen
        !submissionIdForEditFlow
            ? userDeclarationsData?.unforeseenSubmissions?.forEach(
                  (submission) => {
                      submission &&
                          submission?.unforeseenAbsences?.forEach(
                              (unforeseenAbsence: UnforeseenAbsence) => {
                                  disabledDates.push(
                                      new Date(unforeseenAbsence.startDate)
                                  );
                              }
                          );
                  }
              )
            : null;

        // disabled dates from planned
        userDeclarationsData?.plannedSubmissions?.forEach((submission) => {
            submission &&
                submission?.dateRanges?.forEach((range) => {
                    for (
                        let d = new Date(range.startDate);
                        d <= new Date(range.endDate);
                        d.setDate(d.getDate() + 1)
                    ) {
                        disabledDates.push(new Date(d));
                    }
                });
        });

        // disabling dates selected in other rows on same page
        values?.unforeseenAbsences?.forEach((unforeseenAbsence) => {
            disabledDates.push(unforeseenAbsence.startDate);
        });

        return disabledDates;
    }, [
        todayTimestamp,
        userDeclarationsData,
        values,
        values.unforeseenAbsences,
    ]);

    const [disabledDates, setDisabledDates] = useState<Date[]>(
        collectDisabledDates()
    );

    useEffect(() => {
        setDisabledDates(collectDisabledDates());
        // IMPORTANT: do not add collectDisabledRanges to this dependency array
    }, [values.unforeseenAbsences]);

    useEffect(() => {
        if (submissionIdForEditFlow && previousUnforeseenSubmissionData) {
            const updatedUnforeseenAbsences =
                previousUnforeseenSubmissionData.unforeseenAbsences?.map(
                    (unforeseenAbsence) => ({
                        startDate: new Date(unforeseenAbsence.startDate),
                        reasonForAbsence:
                            unforeseenAbsence.reasonForAbsence || '',
                        declarationId:
                            unforeseenAbsence.declarationId || undefined,
                    })
                );
            setFieldValue('unforeseenAbsences', updatedUnforeseenAbsences).then(
                () => {
                    setIsDataSet(true);
                }
            );
        } else {
            setIsDataSet(true);
        }
    }, [
        submissionIdForEditFlow,
        previousUnforeseenSubmissionData,
        setFieldValue,
    ]);

    const renderDateRows = useCallback(() => {
        if (!isDataSet) return null;

        return (
            <>
                {/*EDIT FLOW - previous unforeseen submission data*/}
                {submissionIdForEditFlow
                    ? previousUnforeseenSubmissionData &&
                      previousUnforeseenSubmissionData?.unforeseenAbsences?.map(
                          (unforeseenAbsence) => {
                              if (
                                  !values.declarationIdsToDelete?.includes(
                                      unforeseenAbsence.declarationId
                                  )
                              ) {
                                  const currentAbsence =
                                      values.unforeseenAbsences?.find(
                                          (absence) =>
                                              absence.declarationId ===
                                              unforeseenAbsence.declarationId
                                      );
                                  return (
                                      <UnforeseenDatesRow
                                          key={unforeseenAbsence.declarationId}
                                          index={
                                              unforeseenAbsence.declarationId
                                          }
                                          unforeseenAbsence={currentAbsence}
                                          disabledDates={disabledDates}
                                      />
                                  );
                              }
                          }
                      )
                    : null}

                {/*EDIT FLOW - new date ranges have no declarationId*/}
                {submissionIdForEditFlow
                    ? values?.unforeseenAbsences?.map(
                          (unforeseenAbsence, index) => {
                              if (
                                  unforeseenAbsence.declarationId === undefined
                              ) {
                                  return (
                                      <UnforeseenDatesRow
                                          key={`${index}`}
                                          index={`${index}`}
                                          unforeseenAbsence={{
                                              declarationId:
                                                  unforeseenAbsence.declarationId
                                                      ? unforeseenAbsence.declarationId
                                                      : undefined,
                                              startDate:
                                                  unforeseenAbsence.startDate
                                                      ? new Date(
                                                            unforeseenAbsence.startDate
                                                        )
                                                      : undefined,
                                              reasonForAbsence:
                                                  unforeseenAbsence.reasonForAbsence
                                                      ? unforeseenAbsence.reasonForAbsence
                                                      : '',
                                          }}
                                          disabledDates={disabledDates}
                                      />
                                  );
                              }
                          }
                      )
                    : null}

                {/*CREATE FLOW*/}
                {!submissionIdForEditFlow
                    ? Array.isArray(values.unforeseenAbsences) &&
                      values.unforeseenAbsences.map(
                          (unforeseenAbsence, index) => (
                              <UnforeseenDatesRow
                                  key={
                                      unforeseenAbsence.declarationId ||
                                      `${index}`
                                  }
                                  index={
                                      unforeseenAbsence.declarationId ||
                                      `${index}`
                                  }
                                  unforeseenAbsence={{
                                      declarationId:
                                          unforeseenAbsence.declarationId,
                                      startDate:
                                          submissionIdForEditFlow &&
                                          unforeseenAbsence.startDate !== null
                                              ? new Date(
                                                    unforeseenAbsence.startDate
                                                )
                                              : unforeseenAbsence.startDate,
                                      reasonForAbsence:
                                          unforeseenAbsence.reasonForAbsence,
                                  }}
                                  disabledDates={disabledDates}
                              />
                          )
                      )
                    : null}
            </>
        );
    }, [values, submissionIdForEditFlow, disabledDates, isDataSet]);

    const addAnotherCircumstanceIsDisabled = useCallback(() => {
        return (
            values?.unforeseenAbsences?.some((unforeseenAbsence) => {
                return (
                    !unforeseenAbsence.startDate ||
                    !unforeseenAbsence.reasonForAbsence
                );
            }) ||
            (values.unforeseenAbsences?.length &&
                values.unforeseenAbsences?.length > 4)
        );
    }, [values?.unforeseenAbsences]);

    const headerText = (
        <span>
            {l('_pleaseSelectUnforeseenCircumstance')}
            {l('_pleaseSelectUnforeseenCircumstanceB')}
        </span>
    );

    return (
        <>
            {previousUnforeseenSubmissionDataIsPending ? (
                <Loading loadingType="circular" indeterminate compact={false} />
            ) : (
                <div>
                    {submissionIdForEditFlow
                        ? l('_editPleaseSelectUnforeseenCircumstance')
                        : headerText}
                    {submissionIdForEditFlow &&
                    consecutiveUnforeseenAndSameReason ? (
                        <Notice
                            title=""
                            message={l('_unforeseenDataLossDisclaimer')}
                            status="error"
                            closeable={false}
                            className="mx-0"
                            style={{
                                minWidth: '300px',
                                maxWidth: '100%',
                                width: '100%',
                            }}
                        ></Notice>
                    ) : null}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 0.2fr',
                        }}
                    >
                        <h2 className="ap-typography-heading-s">
                            {submissionIdForEditFlow
                                ? l('_editUnforeseenCircumstances')
                                : l('_unforeseenCircumstances')}
                        </h2>
                        <div className={'d-flex align-items-center'}>
                            <div className={'me-1'}>
                                <h2 className="ap-typography-heading-s">
                                    {l('_applicableDate')}
                                </h2>
                            </div>
                            <div>
                                <Tooltip
                                    trigger="hover"
                                    position="top-left"
                                    distance={4}
                                    id="disabled dates tooltip"
                                    appendAfterTarget={true}
                                    content={l('_disabledDatesWarning')}
                                >
                                    <Button
                                        add
                                        icon="icon-information-outline"
                                        compact
                                        kind={'text'}
                                    >
                                        {l('_disabledDatesWarning')}
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                    <div
                        className="change-declarationType-main"
                        style={{ flexDirection: 'column' }}
                    >
                        {renderDateRows()}
                    </div>
                    <div className="add-dates-div">
                        <Button
                            kind="secondary"
                            onClick={addNewRow}
                            disabled={addAnotherCircumstanceIsDisabled()}
                        >
                            {l('_addCircumstance')}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default UnforeseenSubmissionStepThree;
